import React, { useState, useRef, useEffect } from 'react'
import { Button, message, Card, Input, Spin as AntdSpin, Modal } from 'antd'
import { EditOutlined } from '@ant-design/icons'
import RichTextEditor from '../richTextEditor'
import { cleanHtmlContent } from '../../utils/htmlCleaner'

export const ContentGenerator = ({
  title,
  inputPlaceholder,
  generateButtonText,
  regenerateButtonText,
  editButtonText,
  onGenerate,
  onRegenerate,
  onCopy,
  onSendEmail,
  onSendTestEmail,
  onRefine,
  sendEmailButtonText = null,
  sendTestEmailButtonText = null,
  loadTestDataButtonText = null,
  onLoadTestData = null
}) => {
  const [topic, setTopic] = useState('')
  const [contentHtml, setContentHtml] = useState('')
  const [loading, setLoading] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [editedContent, setEditedContent] = useState('')
  const [progress, setProgress] = useState([])
  const editorContentRef = useRef('')
  const eventSourceRef = useRef(null)
  const [refinementPrompt, setRefinementPrompt] = useState('')
  const [isRefining, setIsRefining] = useState(false)
  const [refinedContent, setRefinedContent] = useState('')
  const [showRefinementOptions, setShowRefinementOptions] = useState(false)

  const handleGenerate = async (regenerate = false) => {
    setLoading(true)
    setProgress([])
    
    try {
      const result = await onGenerate(topic, regenerate, (stepData) => {
        setProgress(prev => [...prev, stepData])
      })
      
      if (result.success) {
        const cleanedHtml = cleanHtmlContent(result.content_html)
        setContentHtml(cleanedHtml)
        message.success(result.message || `${title} generated successfully!`)
      } else {
        message.error(result.message || `Failed to generate ${title}. Please try again.`)
      }
    } catch (err) {
      console.error(`Error generating ${title}:`, err)
      message.error(err.message || `Failed to generate ${title}. Please try again.`)
    } finally {
      setLoading(false)
    }
  }

  const handleCopy = async () => {
    try {
      const cleanedHtml = cleanHtmlContent(contentHtml)

      if (typeof window !== 'undefined' && window.ClipboardItem) {
        const clipboardItem = new window.ClipboardItem({
          'text/html': new Blob([cleanedHtml], { type: 'text/html' }),
          'text/plain': new Blob([cleanedHtml], { type: 'text/plain' })
        })

        await navigator.clipboard.write([clipboardItem])
      } else {
        navigator.clipboard.writeText(cleanedHtml)
      }
      message.success('Copied to clipboard')
    } catch (err) {
      const cleanedHtml = cleanHtmlContent(contentHtml)
      navigator.clipboard.writeText(cleanedHtml)
      message.success('Copied to clipboard')
    }
  }

  const handleRegenerate = () => {
    Modal.confirm({
      title: `Regenerate ${title}`,
      content: 'This will replace the current content. Continue?',
      onOk: () => handleGenerate(true)
    })
  }

  const handleEdit = () => {
    setEditedContent(contentHtml)
    editorContentRef.current = contentHtml
    setIsEditing(true)
  }

  const handleSave = () => {
    const finalContent = editorContentRef.current || editedContent
    setContentHtml(finalContent)
    setIsEditing(false)
    message.success('Changes saved successfully!')
  }

  const handleCancel = () => {
    setEditedContent(contentHtml)
    editorContentRef.current = ''
    setIsEditing(false)
    message.info('Changes discarded')
  }

  const handleEditorChange = (html) => {
    editorContentRef.current = html
  }

  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close()
      }
    }
  }, [])

  const handleSendEmail = () => {
    if (onSendEmail) {
      Modal.confirm({
        title: 'Send Email',
        content: 'Are you sure you want to send this email?',
        okText: 'Send',
        cancelText: 'Cancel',
        onOk: async () => {
          try {
            const subject = title
            const response = await onSendEmail({ content: contentHtml, subject: subject })
            if (response.success === true) {
              message.success('Email sent successfully!')
            } else {
              message.error('Failed to send email. Please try again.')
            }
          } catch (err) {
            console.error('Error sending email:', err)
            message.error('Failed to send email. Please try again.')
          }
        }
      })
    }
  }

  const handleSendTestEmail = async () => {
    if (onSendTestEmail) {
      try {
        const subject = `Test ${title}`
        const response = await onSendTestEmail({ html: contentHtml, subject: subject })
        if (response.success === true) {
          message.success('Test email sent successfully!')
        } else {
          message.error('Failed to send test email. Please try again.')
        }
      } catch (err) {
        console.error('Error sending test email:', err)
        message.error('Failed to send test email. Please try again.')
      }
    }
  }

  const handleRefine = async () => {
    if (!refinementPrompt.trim()) {
      message.error('Please enter a refinement prompt')
      return
    }

    setIsRefining(true)
    setProgress([])
    try {
      const response = await onRefine(
        contentHtml,
        refinementPrompt,
        (stepData) => {
          setProgress(prev => [...prev, stepData])
        }
      )
      // Clean and decode the refined content
      const cleanedContent = cleanHtmlContent(response.refined_content)
      setRefinedContent(cleanedContent)
      setShowRefinementOptions(true)
    } catch (err) {
      console.error('Error refining content:', err)
      message.error('Failed to refine content. Please try again.')
    } finally {
      setIsRefining(false)
    }
  }

  const handleAccept = () => {
    setContentHtml(refinedContent)
    setEditedContent(refinedContent)
    editorContentRef.current = refinedContent
    setShowRefinementOptions(false)
    setRefinementPrompt('')
    setRefinedContent('')
    message.success('Content refined successfully!')
  }

  const handleDecline = () => {
    setShowRefinementOptions(false)
    setRefinementPrompt('')
    setRefinedContent('')
    message.info('Refinement declined')
  }

  const handleLoadTestData = () => {
    if (onLoadTestData) {
      onLoadTestData(setContentHtml)
    }
  }

  return (
    <div>
      {(loading || isRefining) && (
        <div className="loading-overlay">
          <div className="loading-popup">
            <AntdSpin size="large" style={{ marginBottom: '16px' }} />
            <div className="progress-steps">
              {progress.map((step, index) => (
                <div key={index} className={`progress-step step-${step.step} ${step.status}`}>
                  {step.message}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      <div className="container">
        <h2>{title}</h2>
        <div className="input-section">
          <Input
            placeholder={inputPlaceholder}
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            style={{ marginBottom: '16px', width: '100%' }}
          />
        </div>
        <div className="actions">
          <Button type="primary" onClick={() => handleGenerate(false)} loading={loading}>
            {generateButtonText}
          </Button>
          <Button onClick={handleRegenerate} disabled={!contentHtml || loading}>
            {regenerateButtonText}
          </Button>
          <Button onClick={handleCopy} disabled={!contentHtml}>
            Copy Content
          </Button>
          <Button onClick={handleEdit} disabled={!contentHtml} icon={<EditOutlined />} style={{ marginRight: '8px' }}>
            {editButtonText}
          </Button>
          {loadTestDataButtonText && (
            <Button onClick={handleLoadTestData} style={{ display: 'none'}}>
              {loadTestDataButtonText}
            </Button>
          )}
          {sendTestEmailButtonText && (
            <Button onClick={handleSendTestEmail} disabled={!contentHtml}>
              {sendTestEmailButtonText}
            </Button>
          )}
          {sendEmailButtonText && (
            <Button onClick={handleSendEmail} disabled={!contentHtml}>
              {sendEmailButtonText}
            </Button>
          )}
        </div>
        {contentHtml && (
          <Card className="content-card">
            {isEditing ? (
              <div>
                {!showRefinementOptions ? (
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
                      <Button type="primary" onClick={handleSave}>
                        Save Changes
                      </Button>
                    </div>
                    <RichTextEditor
                      value={editedContent}
                      onChange={handleEditorChange}
                      placeholder={`Edit your ${title} content here...`}
                      style={{ marginBottom: '16px' }}
                    />
                    <div style={{ marginBottom: '12px', marginTop: '24px' }}>
                      <Input.TextArea
                        placeholder="Enter your refinement prompt (e.g., 'Make it more detailed about nutrition', 'Add bullet points', 'Focus on weight loss')"
                        value={refinementPrompt}
                        onChange={(e) => setRefinementPrompt(e.target.value)}
                        rows={3}
                        style={{ marginBottom: '12px' }}
                      />
                      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                        <Button
                          type="primary"
                          onClick={handleRefine}
                          loading={isRefining}
                          disabled={!refinementPrompt.trim()}
                        >
                          Refine
                        </Button>
                        <Button onClick={handleCancel}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={{ marginBottom: '16px' }}>
                    <RichTextEditor
                      value={refinedContent}
                      onChange={(html) => setRefinedContent(html)}
                      placeholder={`Edit your refined ${title} content here...`}
                      style={{ marginBottom: '16px' }}
                    />
                    <div style={{ textAlign: 'right' }}>
                      <Button onClick={handleDecline} style={{ marginRight: '8px' }}>
                        Decline
                      </Button>
                      <Button type="primary" onClick={handleAccept}>
                        Accept
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div
                className="content-preview"
                dangerouslySetInnerHTML={{ __html: contentHtml }}
              />
            )}
          </Card>
        )}
      </div>
    </div>
  )
}
